import { Link } from 'react-router-dom';

function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-gray-900 text-white py-6">
        <div className="container-custom px-4 sm:px-6 lg:px-8">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-gradient-to-br from-grass-500 to-grass-600 rounded-xl flex items-center justify-center">
              <svg
                className="w-6 h-6 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                />
              </svg>
            </div>
            <span className="text-xl font-bold">TurfPilot</span>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="container-custom px-4 sm:px-6 lg:px-8 py-12 max-w-4xl">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">
          Privacy Policy
        </h1>
        <p className="text-gray-500 mb-8">Last Updated: January 18, 2025</p>

        <div className="prose prose-gray max-w-none">
          <p className="text-lg text-gray-600 mb-8">
            TurfPilot is a service provided by ACID Engineering LLC, a Florida
            limited liability company doing business as TurfPilot
            (&quot;we,&quot; &quot;us,&quot; or &quot;our&quot;). This Privacy
            Policy explains how we collect, use, disclose, and safeguard your
            information when you use our mobile application and website
            (collectively, the &quot;Service&quot;).
          </p>

          <p className="text-gray-600 mb-8">
            Please read this Privacy Policy carefully. By using the Service, you
            agree to the collection and use of information in accordance with
            this policy. If you do not agree with the terms of this Privacy
            Policy, please do not access the Service.
          </p>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              1. Information We Collect
            </h2>

            <h3 className="text-xl font-medium text-gray-800 mt-6 mb-3">
              1.1 Personal Information You Provide
            </h3>
            <p className="text-gray-600 mb-4">
              When you register for an account or use our Service, we may
              collect:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-4">
              <li>
                <strong>Account Information:</strong> Name, email address, and
                profile picture (if provided through Google sign-in)
              </li>
              <li>
                <strong>Property Information:</strong> Home address, property
                size, GPS coordinates, grass type, soil type, sun exposure,
                irrigation system details, and other lawn-related information
                you provide
              </li>
              <li>
                <strong>Photos and Images:</strong> Lawn photos you upload,
                including any embedded metadata (EXIF data) such as date, time,
                and location information
              </li>
              <li>
                <strong>User Content:</strong> Notes, observations, and other
                content you create within the application
              </li>
              <li>
                <strong>Waitlist Information:</strong> Email address when you
                sign up for our waitlist
              </li>
            </ul>

            <h3 className="text-xl font-medium text-gray-800 mt-6 mb-3">
              1.2 Information Collected Automatically
            </h3>
            <p className="text-gray-600 mb-4">
              When you access our Service, we automatically collect:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-4">
              <li>
                <strong>Device Information:</strong> Device type, operating
                system, unique device identifiers, and mobile network
                information
              </li>
              <li>
                <strong>Usage Data:</strong> App features used, time spent in
                the application, and interaction patterns
              </li>
              <li>
                <strong>Log Data:</strong> IP address, browser type,
                referring/exit pages, and date/time stamps
              </li>
              <li>
                <strong>Location Data:</strong> With your permission, precise
                location data from your device to provide location-based
                recommendations
              </li>
            </ul>

            <h3 className="text-xl font-medium text-gray-800 mt-6 mb-3">
              1.3 Information from Third Parties
            </h3>
            <p className="text-gray-600 mb-4">
              We may receive information from:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>
                <strong>Google Sign-In:</strong> If you choose to sign in with
                Google, we receive your name, email address, and profile picture
                from Google
              </li>
              <li>
                <strong>Weather Services:</strong> We may obtain weather data
                for your location to provide weather-smart recommendations
              </li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              2. How We Use Your Information
            </h2>
            <p className="text-gray-600 mb-4">
              We use the information we collect to:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Provide, operate, and maintain the Service</li>
              <li>Create and manage your account</li>
              <li>Generate personalized lawn care plans and recommendations</li>
              <li>
                Analyze lawn photos using artificial intelligence to identify
                issues and provide treatment recommendations
              </li>
              <li>
                Send weather-based notifications and reminders for lawn care
                tasks
              </li>
              <li>Track your lawn&apos;s progress over time</li>
              <li>Improve and optimize our Service</li>
              <li>Communicate with you about updates, features, and support</li>
              <li>
                Detect, prevent, and address technical issues and security
                threats
              </li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              3. AI-Powered Features
            </h2>
            <p className="text-gray-600 mb-4">
              Our Service uses artificial intelligence to analyze lawn photos
              and provide recommendations. When you upload photos:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>
                Images are processed by AI systems to identify lawn conditions,
                diseases, pests, and weeds
              </li>
              <li>
                AI analysis results are stored with your photos to provide
                personalized recommendations
              </li>
              <li>
                Your photos may be used to improve our AI models, but only in
                anonymized or aggregated form
              </li>
              <li>
                You retain ownership of your photos; we only use them to provide
                the Service
              </li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              4. How We Share Your Information
            </h2>
            <p className="text-gray-600 mb-4">
              We may share your information in the following circumstances:
            </p>

            <h3 className="text-xl font-medium text-gray-800 mt-6 mb-3">
              4.1 Service Providers
            </h3>
            <p className="text-gray-600 mb-4">
              We share information with third-party vendors who perform services
              on our behalf, including:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-4">
              <li>
                <strong>Cloudflare:</strong> Cloud infrastructure, content
                delivery, security, and AI processing services
              </li>
              <li>
                <strong>Google:</strong> Authentication services
              </li>
              <li>
                <strong>Payment Processors:</strong> To process subscription
                payments (when applicable)
              </li>
              <li>
                <strong>Email Service Providers:</strong> To send transactional
                emails and notifications
              </li>
            </ul>

            <h3 className="text-xl font-medium text-gray-800 mt-6 mb-3">
              4.2 Legal Requirements
            </h3>
            <p className="text-gray-600 mb-4">
              We may disclose your information if required by law or in response
              to valid legal process, such as a subpoena, court order, or
              government request.
            </p>

            <h3 className="text-xl font-medium text-gray-800 mt-6 mb-3">
              4.3 Business Transfers
            </h3>
            <p className="text-gray-600 mb-4">
              If we are involved in a merger, acquisition, or sale of assets,
              your information may be transferred as part of that transaction.
            </p>

            <h3 className="text-xl font-medium text-gray-800 mt-6 mb-3">
              4.4 With Your Consent
            </h3>
            <p className="text-gray-600">
              We may share your information for other purposes with your
              explicit consent.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              5. Data Storage and Security
            </h2>
            <p className="text-gray-600 mb-4">
              We implement appropriate technical and organizational measures to
              protect your personal information:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Data is encrypted in transit using TLS/SSL</li>
              <li>Authentication tokens are stored as secure hashes</li>
              <li>
                Access to personal data is restricted to authorized personnel
              </li>
              <li>
                We use Cloudflare&apos;s global infrastructure for secure data
                storage and processing
              </li>
              <li>Regular security assessments and updates are performed</li>
            </ul>
            <p className="text-gray-600 mt-4">
              However, no method of transmission over the Internet or electronic
              storage is 100% secure. While we strive to protect your
              information, we cannot guarantee absolute security.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              6. Data Retention
            </h2>
            <p className="text-gray-600 mb-4">
              We retain your personal information for as long as your account is
              active or as needed to provide the Service. We may retain certain
              information after account deletion for:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Compliance with legal obligations</li>
              <li>Resolution of disputes</li>
              <li>Enforcement of agreements</li>
              <li>Legitimate business purposes</li>
            </ul>
            <p className="text-gray-600 mt-4">
              Session data and expired tokens are automatically deleted through
              scheduled cleanup processes.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              7. Your Rights and Choices
            </h2>
            <p className="text-gray-600 mb-4">
              Depending on your location, you may have certain rights regarding
              your personal information:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>
                <strong>Access:</strong> Request a copy of the personal
                information we hold about you
              </li>
              <li>
                <strong>Correction:</strong> Request correction of inaccurate
                personal information
              </li>
              <li>
                <strong>Deletion:</strong> Request deletion of your personal
                information, subject to certain exceptions
              </li>
              <li>
                <strong>Portability:</strong> Request a copy of your data in a
                portable format
              </li>
              <li>
                <strong>Opt-Out:</strong> Opt out of certain data processing
                activities
              </li>
            </ul>
            <p className="text-gray-600 mt-4">
              To exercise these rights, please contact us at the email address
              provided below. We will respond to your request within the
              timeframe required by applicable law.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              8. Mobile Application
            </h2>
            <p className="text-gray-600 mb-4">Our mobile application may:</p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>
                <strong>Store Data Locally:</strong> Some data is stored on your
                device for offline access. This data is synced when you
                reconnect to the internet.
              </li>
              <li>
                <strong>Request Permissions:</strong> We may request access to
                your camera, photo library, and location. You can manage these
                permissions in your device settings.
              </li>
              <li>
                <strong>Send Push Notifications:</strong> With your consent, we
                send push notifications for weather alerts and task reminders.
                You can disable these in your device settings.
              </li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              9. Children&apos;s Privacy
            </h2>
            <p className="text-gray-600">
              Our Service is not intended for children under the age of 13. We
              do not knowingly collect personal information from children under
              13. If we become aware that we have collected personal information
              from a child under 13, we will take steps to delete that
              information promptly.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              10. International Data Transfers
            </h2>
            <p className="text-gray-600">
              Your information may be transferred to and processed in countries
              other than your own, including the United States. These countries
              may have different data protection laws. By using the Service, you
              consent to the transfer of your information to these countries. We
              take steps to ensure that your data receives an adequate level of
              protection in the jurisdictions in which we process it.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              11. California Privacy Rights
            </h2>
            <p className="text-gray-600 mb-4">
              If you are a California resident, you have additional rights under
              the California Consumer Privacy Act (CCPA):
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>
                The right to know what personal information we collect, use, and
                disclose
              </li>
              <li>
                The right to request deletion of your personal information
              </li>
              <li>
                The right to opt-out of the sale of personal information (we do
                not sell personal information)
              </li>
              <li>
                The right to non-discrimination for exercising your privacy
                rights
              </li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              12. Changes to This Privacy Policy
            </h2>
            <p className="text-gray-600">
              We may update this Privacy Policy from time to time. We will
              notify you of any changes by posting the new Privacy Policy on
              this page and updating the &quot;Last Updated&quot; date. For
              significant changes, we may also notify you via email or through
              the Service. Your continued use of the Service after any changes
              constitutes acceptance of the updated Privacy Policy.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              13. Contact Us
            </h2>
            <p className="text-gray-600 mb-4">
              If you have questions or concerns about this Privacy Policy or our
              privacy practices, please contact us at:
            </p>
            <div className="bg-gray-50 p-6 rounded-lg">
              <p className="text-gray-800 font-medium">
                ACID Engineering LLC (d/b/a TurfPilot)
              </p>
              <p className="text-gray-600 mt-2">
                Email:{' '}
                <a
                  href="mailto:privacy@turfpilot.app"
                  className="text-grass-600 hover:text-grass-700"
                >
                  privacy@turfpilot.app
                </a>
              </p>
            </div>
          </section>
        </div>

        {/* Back to home */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <Link
            to="/"
            className="inline-flex items-center text-grass-600 hover:text-grass-700 font-medium"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Back to Home
          </Link>
        </div>
      </main>

      {/* Simple Footer */}
      <footer className="bg-gray-900 text-gray-400 py-8 mt-12">
        <div className="container-custom px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm">
            &copy; {new Date().getFullYear()} TurfPilot. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default PrivacyPolicy;
